import type { StructureType } from '@/rtkApi/mddApiSliceGeneratedV2'

const ROOT_SYSTEM_TYPES: StructureType[] = ['ROOT', 'SYSTEM']
const FOLDER_TYPES: StructureType[] = ['FOLDER', 'FOLDER_WORKFLOW']
const TABLE_TYPES: StructureType[] = ['TABLE', 'TABLE_COLUMN']
const MAPPING_TYPES: StructureType[] = ['MAPPING', 'MAPPING_DEFAULT']
const API_TYPES: StructureType[] = ['API_NODE', 'API_COLLECTION']

export type SearchFilterPreset =
	| 'all'
	| 'rootSystem'
	| 'folders'
	| 'tables'
	| 'mappings'
	| 'mappingRead'
	| 'mappingWrite'
	| 'views'
	| 'workflows'
	| 'diagrams'
	| 'api'

export type SearchFilterPresetKey = Exclude<SearchFilterPreset, 'all'>

export const SEARCH_FILTER_PRESETS: Record<
	SearchFilterPresetKey,
	StructureType[]
> = {
	rootSystem: ROOT_SYSTEM_TYPES,
	folders: FOLDER_TYPES,
	tables: TABLE_TYPES,
	mappings: MAPPING_TYPES,
	mappingRead: ['MAPPING_READ' as StructureType],
	mappingWrite: ['MAPPING_WRITE' as StructureType],
	views: ['VIEW' as StructureType],
	workflows: ['WORKFLOW' as StructureType],
	diagrams: ['DIAGRAM' as StructureType],
	api: API_TYPES,
}

function sameTypes(a: StructureType[] | undefined, b: StructureType[]) {
	if (!a || a.length !== b.length) return false
	return b.every((t: StructureType) => a.includes(t))
}

export function typesToPreset(
	types: StructureType[] | undefined,
): SearchFilterPreset {
	if (!types || types.length === 0) return 'all'
	if (sameTypes(types, ROOT_SYSTEM_TYPES)) return 'rootSystem'
	if (sameTypes(types, FOLDER_TYPES)) return 'folders'
	if (sameTypes(types, TABLE_TYPES)) return 'tables'
	if (sameTypes(types, MAPPING_TYPES)) return 'mappings'
	if (sameTypes(types, ['MAPPING_READ' as StructureType])) return 'mappingRead'
	if (sameTypes(types, ['MAPPING_WRITE' as StructureType])) return 'mappingWrite'
	if (sameTypes(types, ['VIEW' as StructureType])) return 'views'
	if (sameTypes(types, ['WORKFLOW' as StructureType])) return 'workflows'
	if (sameTypes(types, ['DIAGRAM' as StructureType])) return 'diagrams'
	if (sameTypes(types, API_TYPES)) return 'api'
	return 'all'
}

export function presetToTypes(preset: SearchFilterPreset): StructureType[] | undefined {
	if (preset === 'all') return undefined
	return SEARCH_FILTER_PRESETS[preset]
}

/** Types for the given presets (union of all). Empty array = undefined (all types). */
export function presetsToTypes(presets: SearchFilterPresetKey[]): StructureType[] | undefined {
	if (presets.length === 0) return undefined
	const types = presets.flatMap((p) => SEARCH_FILTER_PRESETS[p])
	return types.length === 0 ? undefined : types
}

export const PRESET_OPTIONS: SearchFilterPreset[] = [
	'all',
	'tables',
	'mappings',
	'views',
	'workflows',
	'api',
	'rootSystem',
	'folders',
	'mappingRead',
	'mappingWrite',
	'diagrams',
]

export const PRESET_I18N_KEYS: Record<SearchFilterPreset, string> = {
	all: 'NAVBAR_SEARCH_FILTER_ALL',
	rootSystem: 'NAVBAR_SEARCH_FILTER_ROOT_SYSTEM',
	folders: 'NAVBAR_SEARCH_FILTER_FOLDERS',
	tables: 'NAVBAR_SEARCH_FILTER_TABLES',
	mappings: 'NAVBAR_SEARCH_FILTER_MAPPINGS',
	mappingRead: 'NAVBAR_SEARCH_FILTER_MAPPING_READ',
	mappingWrite: 'NAVBAR_SEARCH_FILTER_MAPPING_WRITE',
	views: 'NAVBAR_SEARCH_FILTER_VIEWS',
	workflows: 'NAVBAR_SEARCH_FILTER_WORKFLOWS',
	diagrams: 'NAVBAR_SEARCH_FILTER_DIAGRAMS',
	api: 'NAVBAR_SEARCH_FILTER_API',
}
